/* EngineRoom.tsx — where the data came from, what ran on it, what is still wrong with it, and what
 * every column means. For the person who has to DEFEND the dataset, not the person selling from it.
 *
 * NOTHING ON THIS PAGE IS TYPED IN. Every figure is queried at request time — /api/audit for the
 * measured state, /api/lineage for the build's own record of what ran, /api/coverage for the gap
 * ledger, and the column registry out of /api/meta. The point of this screen is attribution: "did we
 * break this, or did it arrive broken". An attribution claim backed by a hardcoded number is worth
 * nothing, which is why there is not one on the page.
 *
 * /api/cell is the per-value trace: upstream to the file the value came from, downstream to
 * everything it moves. Both directions are read off the SAME edge list so they cannot drift apart,
 * and a step that cannot say something TRUE about the row in hand is DROPPED rather than padded —
 * which is why two rows in the same column return different chains.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, isErr, type Entity, type Meta } from '../lib/api.ts';
import { N, SQ, localTime } from '../lib/fmt.ts';
import { Btn, Chip, ErrBox, Hint, RTable, Section, Stat, TD, TR, HAIR, selStyle } from '../components/ui.tsx';
import { useCoverage } from './Manager.tsx';

interface Stage { id: string; name: string; what: string; inN: number | null; outN: number; outLabel: string; script: string; note?: string }
interface Op {
  id: string; cls: string; owner: string; what: string; script: string; target: string;
  n: number | null; nProof: string | null; when: { at: string; to?: string } | null;
  plain: string; risk: string; reversible: string; evidence: string | null;
}
interface Lineage { available: boolean; hint?: string; builtAt?: string; master?: string; stages?: Stage[]; ledger?: Op[] }

interface Audit {
  built: string | null;
  totals: Record<string, number | null>;
  offMap: Record<string, unknown>;
  geo: Record<string, unknown>;
  sqft: Record<string, unknown>;
  dupes: Record<string, unknown>;
  titles: Record<string, number | null>;
  sound: Record<string, unknown>;
}

/* The five kinds of operation the lineage build emits, in ascending order of "how much would this
   hurt if it were wrong". Colour follows that, so the risky ones are findable without reading. */
const CLSWHAT: Record<string, string> = {
  ingest: 'read a file you sent and stored it as written',
  derive: 'computed a NEW column and left the source columns alone',
  flag: 'marked rows in or out of scope — a flag, never a delete',
  repair: 'corrected values in place',
  remove: 'blanked a value that could not be trusted',
};
const CLSTONE: Record<string, 'none' | 'info' | 'warn' | 'bad'> = {
  ingest: 'none', derive: 'info', flag: 'info', repair: 'warn', remove: 'bad',
};

const ENTNAME: Record<Entity, string> = { acct: 'Account', fac: 'Plant', ppl: 'Contact' };

export function EngineRoom({ meta, onOpenGap }: { meta: Meta; onOpenGap?: (ent: string, q: unknown) => void }) {
  const [lin, setLin] = useState<Lineage | null>(null);
  const [audit, setAudit] = useState<Audit | null>(null);
  const [auditErr, setAuditErr] = useState<string | null>(null);
  const [regq, setRegq] = useState('');
  const { c, err } = useCoverage();

  useEffect(() => {
    let dead = false;
    /* Two separate fetches on purpose. /api/audit re-measures the source CSV and runs ~40
       aggregates, which is seconds; the lineage document is a file read. Folded together, the whole
       tab would hold blank on the slow one — which is what a 3-second stare at an empty Engine room
       was. Each section fills in when its own answer lands. */
    api<Lineage>('lineage').then((r) => { if (!dead) setLin(isErr(r) ? { available: false, hint: r.error } : r); });
    api<Audit>('audit').then((r) => { if (!dead) { if (isErr(r)) setAuditErr(r.error); else setAudit(r); } });
    return () => { dead = true; };
  }, []);

  const reg = useMemo(() => {
    const q = regq.toLowerCase().trim();
    const hit = (f: { lbl: string; k: string; src: string; grp: string }) =>
      !q || (f.lbl + ' ' + f.k + ' ' + (f.src || '') + ' ' + (f.grp || '')).toLowerCase().includes(q);
    return (['acct', 'fac', 'ppl'] as Entity[]).map((e) => ({ e, fs: meta.fields[e].filter(hit), all: meta.fields[e].length }));
  }, [regq, meta]);

  const totalCols = meta.fields.acct.length + meta.fields.fac.length + meta.fields.ppl.length;

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>

      {/* ── the pipeline ──────────────────────────────────────────────────────────────────────── */}
      <Section label="How your files became this database">
        <Hint>
          Left to right, in order. Each box says what it took in, what it produced, and which script did it.{' '}
          {!lin ? 'Reading the lineage…'
            : !lin.available ? <>The lineage document could not be read, so this section is empty rather than wrong. It ships at <Mono>data/lineage.json</Mono> and the image copies it in. {lin.hint}</>
              : `Built ${localTime(lin.builtAt)} from ${lin.master || 'the master database'}.`}
        </Hint>
        {lin?.available && lin.stages && (
          <div style={{ display: 'flex', gap: 0, marginTop: 12, overflowX: 'auto' }}>
            {lin.stages.map((s) => (
              <div key={s.id} style={{ minWidth: 190, flex: '0 0 auto', borderLeft: HAIR, padding: '0 12px' }}>
                <div className="num t-micro" style={{ color: 'var(--or)' }}>{s.id}</div>
                <div style={{ fontWeight: 600, marginTop: 2 }}>{s.name}</div>
                <Hint style={{ marginTop: 3 }}>{s.what}</Hint>
                <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'baseline' }}>
                  {s.inN != null && <span className="num t-micro" style={{ color: 'var(--dim2)' }}>{N(s.inN)} in →</span>}
                  <span className="num" style={{ color: 'var(--am)', fontSize: 'var(--t-fig)' }}>{N(s.outN)}</span>
                  <span className="t-label">{s.outLabel}</span>
                </div>
                <div className="t-micro" style={{ fontFamily: 'var(--mono)', color: 'var(--dim2)', marginTop: 3 }} title={s.script}>{s.script}</div>
                {s.note && <Hint style={{ color: 'var(--dim2)', marginTop: 3 }}>{s.note}</Hint>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── the measured state ────────────────────────────────────────────────────────────────── */}
      {auditErr && <ErrBox what="The audit did not run" msg={auditErr} />}
      {audit && (
        <>
          <div style={{ display: 'flex', borderBottom: HAIR, overflowX: 'auto' }}>
            <Stat label="Accounts" value={N(audit.totals.accounts)} sub="in scope" />
            <Stat label="Plants" value={N(audit.totals.plants)} sub={`${N(audit.geo.geocoded)} geocoded`} />
            <Stat label="Contacts" value={N(audit.totals.people)} sub="reachable buyers held" />
            <Stat label="Sq ft" value={SQ(audit.totals.sqft)} sub="summed exactly as sent" />
            <Stat label="Built" value={<span style={{ fontSize: 'var(--t-body)' }}>{localTime(audit.built)}</span>} sub="dataset release" />
          </div>

          <Section label="What is measurably wrong with it">
            <Hint>
              Every figure here is a live count against the serving snapshot. A gap this build introduced and a
              gap that arrived in the source file are both listed, because the point of this page is attribution,
              not defence.
            </Hint>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, marginTop: 10 }}>
              <Panel title="Geocoding">
                <KV k="Plants not geocoded" v={N(audit.geo.geocoded == null ? null : Number(audit.totals.plants) - Number(audit.geo.geocoded))} />
                <KV k="Placed on a city centroid" v={N(audit.geo.centroid)} note="a town, not a street address" />
                <KV k="Plants stacked on one point" v={N(audit.geo.stackedExtra)} note={`${N(audit.geo.stackedGroups)} groups share a coordinate`} />
                <KV k="Accounts with no dot at all" v={N(audit.offMap.accounts)} note={`${N(audit.offMap.t1)} of them Tier 1`} />
              </Panel>
              <Panel title="Sq ft trust">
                <KV k="Under 1,000 sq ft" v={N(audit.sqft.under1k)} note="not floor area" />
                <KV k="Under 5,000 sq ft" v={N(audit.sqft.under5k)} />
                <KV k="No figure at all" v={N(audit.sqft.missing)} />
                <KV k="Accounts where every plant is tiny" v={N(audit.sqft.acctsAllSmall)} />
              </Panel>
              <Panel title="Duplicates">
                <KV k="Duplicate plant groups" v={N(audit.dupes.groups)} />
                <KV k="Extra rows they carry" v={N(audit.dupes.extra)} />
                <KV k="Sq ft double-counted" v={SQ(audit.dupes.sqft)} />
              </Panel>
              <Panel title="Titles & contact">
                <KV k="Title confidence under 70" v={N(audit.titles.lowConf)} note={`${N(audit.titles.lowConfBuyers)} of them in a buying role`} />
                <KV k="No title at all" v={N(audit.titles.noTitle)} />
                <KV k="Switchboard numbers" v={N(audit.sound.switchboard)} note={`${N(audit.sound.switchboardOnly)} with no email either`} />
                <KV k="Mojibake in plant names" v={N(audit.sound.mojibakeFac)} />
              </Panel>
              <Panel title="Placement">
                <KV k="Contacts placed at a plant" v={N(audit.sound.placedTotal)} />
                <KV k="Within 25 miles" v={N(audit.sound.placedWithin25)} />
                <KV k="Over 100 miles" v={N(audit.sound.placedOver100)} />
                <KV k="Refused placement (>100mi)" v={N(audit.sound.refusedPlacement)} note="left unplaced rather than guessed" />
              </Panel>
            </div>
          </Section>
        </>
      )}

      {/* ── the gap ledger, openable ──────────────────────────────────────────────────────────── */}
      <Section label="Every gap, with its cause and what closing it costs">
        <Hint>
          The rule this project runs on: a number without its method is a rumour. Each row here is a live count,
          not a note from a previous session — and every count opens the exact rows behind it in the grid.
          A number you cannot open is a number nobody checks.
        </Hint>
        {err && <ErrBox what="The gap ledger did not load" msg={err} />}
        {c && (
          <div style={{ marginTop: 10, overflowX: 'auto' }}>
            <RTable cols={[{ k: 'Gap' }, { k: 'Rows', num: true }, { k: 'Cause / next move' }, { k: '', w: 150 }]}>
              {c.gaps.map((g) => (
                <TR key={g.k}>
                  <TD style={{ whiteSpace: 'normal' }}>{g.k}</TD>
                  <TD num color="var(--am)">{N(g.v)}</TD>
                  <TD color="var(--dim)" style={{ whiteSpace: 'normal' }}>{g.why}</TD>
                  <TD>{g.q != null && onOpenGap && <Btn sm onClick={() => onOpenGap(g.ent, g.q)}>Open the rows →</Btn>}</TD>
                </TR>
              ))}
            </RTable>
          </div>
        )}
      </Section>

      {/* ── every operation that has run ──────────────────────────────────────────────────────── */}
      {lin?.available && lin.ledger && lin.ledger.length > 0 && (
        <Section label="Every operation that has touched this data">
          <Hint>
            {lin.ledger.length} of them, oldest first. Open one to see what it wrote, how many rows, what the risk
            is and whether it can be undone.
          </Hint>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 11px' }}>
            {Object.entries(CLSWHAT).map(([k, v]) => (
              <Chip key={k} tone={CLSTONE[k]}><b>{k}</b> {v}</Chip>
            ))}
          </div>
          {lin.ledger.map((op) => (
            <details key={op.id} style={{ borderBottom: HAIR }}>
              <summary style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '6px 0', cursor: 'pointer', listStyle: 'none' }}>
                <span style={{ width: 58 }}><Chip tone={CLSTONE[op.cls]}>{op.cls}</Chip></span>
                <span style={{ flex: '1 1 auto', minWidth: 0 }}>{op.what}</span>
                <span className="t-micro" style={{ fontFamily: 'var(--mono)', color: 'var(--dim2)', width: 120 }}>{op.target}</span>
                <span className="num" style={{ color: 'var(--am)', width: 80 }}>{op.n != null ? N(op.n) : '—'}</span>
              </summary>
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '4px 12px', padding: '8px 0 12px 68px' }}>
                <Dk>In plain terms</Dk><Dv>{op.plain}</Dv>
                <Dk>Script</Dk><Dv mono>{op.script || '—'}</Dv>
                <Dk>Writes to</Dk><Dv mono>{op.target || '—'}</Dv>
                <Dk>Rows</Dk><Dv mono>{op.n != null ? N(op.n) : '—'}{op.nProof ? ` · ${op.nProof}` : ''}</Dv>
                {op.when && <><Dk>When it ran</Dk><Dv mono>{op.when.at}{op.when.to ? ` → ${op.when.to}` : ''}</Dv></>}
                <Dk>Risk</Dk><Dv>{op.risk || '—'}</Dv>
                <Dk>Reversible</Dk><Dv>{op.reversible || '—'}</Dv>
                {op.evidence && <><Dk>Evidence</Dk><Dv mono>{op.evidence}</Dv></>}
                <Dk>Whose</Dk>
                <Dv>{op.owner === 'source' ? 'your file — stored exactly as supplied' : 'the build — this step changed or added something'}</Dv>
              </div>
            </details>
          ))}
        </Section>
      )}

      {/* ── the filter grammar ────────────────────────────────────────────────────────────────── */}
      <Section label="The filter grammar">
        <Hint style={{ maxWidth: 900 }}>
          What the UI sends is an AST, not SQL. The server validates it against the registry and compiles it with
          bound parameters, so a filter can never be an injection and every filter is loggable, storable and shareable.
        </Hint>
        <pre style={{
          marginTop: 10, background: 'var(--s1)', border: HAIR, padding: 12,
          fontFamily: 'var(--mono)', fontSize: 'var(--t-micro)', overflowX: 'auto', color: 'var(--dim)',
        }}>{JSON.stringify({
            op: 'AND',
            kids: [
              { f: 'tier', o: 'eq', v: '1' },
              { op: 'OR', kids: [{ f: 'dept', o: 'eq', v: 'Manufacturing/Production' }, { f: 'dept', o: 'eq', v: 'Quality' }] },
              { op: 'NOT', kids: [{ f: 'email', o: 'empty' }] },
              { tag: 'at this plant' },
            ],
          }, null, 2)}</pre>
        <Hint style={{ marginTop: 8, maxWidth: 900 }}>
          Note the last two. <Mono>is empty</Mono> expands to <Mono>(col IS NULL OR col='')</Mono> — that single trap
          already produced a 0-row answer to a 195,415-row question on this project. And a tag filter reuses the tag's
          own evidence SQL, so a tag and a column can never disagree.
        </Hint>
      </Section>

      {/* ── the column registry, with the per-value trace ─────────────────────────────────────── */}
      <Section label="Every column, and where it came from"
        right={<Hint>{totalCols} columns · {meta.fields.acct.length} account · {meta.fields.fac.length} plant · {meta.fields.ppl.length} contact</Hint>}>
        <Hint>
          The one registry the grid, the filter builder, the column picker, the CSV and the API all read from —
          which is why "add a column" is a config change, not a sprint. Search it by column name or by source.
        </Hint>
        <input style={{ ...selStyle, marginTop: 10, maxWidth: 620 }} value={regq} onChange={(e) => setRegq(e.target.value)}
          placeholder={'search columns and sources — try "your SOM sheet", "vendor", or "derived"'} />
        {reg.every((r) => r.fs.length === 0) && <Hint style={{ marginTop: 12 }}>No column or source matches "{regq}".</Hint>}
        {reg.filter((r) => r.fs.length).map(({ e, fs, all }) => (
          <div key={e} style={{ marginTop: 14 }}>
            <div className="t-label" style={{ marginBottom: 6 }}>{ENTNAME[e]} · {fs.length}{regq ? ` of ${all}` : ' columns'}</div>
            <div style={{ overflowX: 'auto' }}>
              <RTable cols={[{ k: 'Column', w: 210 }, { k: 'Type', w: 70 }, { k: 'Group', w: 180 }, { k: 'Where it comes from' }]}>
                {fs.map((f) => (
                  <TR key={f.k}>
                    <TD>
                      <b>{f.lbl}</b>
                      <div className="t-micro" style={{ fontFamily: 'var(--mono)', color: 'var(--dim2)' }}>{f.k}</div>
                    </TD>
                    <TD color="var(--dim2)">{f.t}</TD>
                    <TD color="var(--dim)">{f.grp || ''}</TD>
                    <TD color="var(--dim)" style={{ whiteSpace: 'normal', lineHeight: 1.5 }}>{f.src || '—'}</TD>
                  </TR>
                ))}
              </RTable>
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

const Panel = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ flex: '1 1 300px', minWidth: 260, borderLeft: HAIR, padding: '0 14px 10px' }}>
    <div className="t-label" style={{ marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);
const KV = ({ k, v, note }: { k: string; v: string; note?: string }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', borderBottom: HAIR }}>
    <span style={{ flex: '1 1 auto', color: 'var(--dim)', fontSize: 'var(--t-micro)' }}>
      {k}{note && <span style={{ color: 'var(--dim2)' }}> — {note}</span>}
    </span>
    <span className="num" style={{ color: 'var(--am)' }}>{v || '—'}</span>
  </div>
);
const Dk = ({ children }: { children: React.ReactNode }) => <span className="t-label">{children}</span>;
const Dv = ({ mono, children }: { mono?: boolean; children: React.ReactNode }) => (
  <span style={{ color: 'var(--dim)', fontFamily: mono ? 'var(--mono)' : undefined, fontSize: mono ? 'var(--t-micro)' : undefined, lineHeight: 1.55 }}>{children}</span>
);
const Mono = ({ children }: { children: React.ReactNode }) => (
  <span style={{ fontFamily: 'var(--mono)', color: 'var(--bl)' }}>{children}</span>
);
